import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
// context
import { useQAContext } from 'src/containers/AddSkuPage/QuestionsFlowPage/Context';
// components
import { RadioGroupV2QA } from 'src/components/HTKit/Forms/RadioGroupV2QAFlow';
import { CheckboxGroupV2QA } from 'src/components/HTKit/Forms/CheckboxV2QA';
import TextAreaV2QAFlow from 'HTKit/Forms/TextAreaV2QAFlow';

// sku data & constants & helpers
import {
  CHECKBOX,
  DEFAULT_GROUP,
  RADIO,
  TEXTAREA,
} from 'src/containers/AddSkuPage/QuestionsFlowPage/constants';
import { getQADataById } from 'src/containers/AddSkuPage/QuestionsFlowPage/Parts/QAPage/utils';
// actions
import {
  updateToNewQA,
  addAnswer,
  updatePrice,
  addTempAnswerToQue,
  removeTempAnswerToQue,
} from 'src/containers/AddSkuPage/QuestionsFlowPage/actions';

/**
 * Since we can radio or checkbox etc... Lets have it in the same file, but not conflate the
 * overall parent component; QuestionAnswerDisplay
 *
 * @param props
 *  - DATA - data schema object
 *  - launchAdditionalInfoDisplay - this takes the additional info, and passes into parent page to deal with.
 * @constructor
 */
const UISelectionDisplay = ({ DATA, launchAdditionalInfoDisplay = () => {} }) => {
  const [contextState, contextDispatch] = useQAContext();
  const currentQuestionFlow = DATA[contextState.currentQuestion] || {};
  const { currentGroup } = contextState;

  /**
   * Fast fix for checkboxes not allowing for required or not.
   * Question needs to be not required AND have a defaultPath. Why?
   *  --- we don't want to affect previous implementations and we can do this by
   *      filtering by the updated Q w/ a defaultPath. It's a key that doesn't exist on others.
   *
   * We "could" just put this particular answer last "add to cart", that would solve it.
   */
  const determineDefaultObject = () => {
    const required = get(currentQuestionFlow, 'required', 0);
    const defaultPath = get(currentQuestionFlow, 'defaultPath', 0);

    return !required && defaultPath ? { path: currentQuestionFlow.defaultPath } : {};
  };

  /**
   * Helper that updates our flow by taking in the selected answer. This will be our
   *
   * Note: Because an answer can derive different answers, we need to handle
   * ie: id, {id, value}, or [id, id, id];
   * @param callbackValue - id, {id, value}, or [id, id, id];
   */
  function udpateQAFlow(callbackValue) {
    /* pass in our 'next' decision plus the subgroup */
    const getQAObject = getQADataById({ callbackValue, currentQuestionFlow, currentGroup });
    /* all same answers to questions MUST have same path/group, so grabbing first one is fine */
    const isFirstAnswer = get(getQAObject, 'answers.0', determineDefaultObject());
    /* break out each action as a unit of operation. If on last step OR force stop(ie. other w/o value), don't update */
    if (callbackValue) {
      contextDispatch(
        updateToNewQA({
          ...DATA[isFirstAnswer.path],
          group: get(isFirstAnswer, 'group', DEFAULT_GROUP),
        }),
      );
      contextDispatch(addAnswer(getQAObject));
      contextDispatch(updatePrice());
    }
  }

  /**
   * We need to update the temp answer que either by adding or removing items from it.
   * Cleared out once a user navigates off the question.
   * @param queItem {add: string|number, remove: string|number}
   */
  function updateAnswerTempQue({ add = false, remove = false }) {
    if (add) {
      const getQAObject = getQADataById({ callbackValue: add, currentQuestionFlow, currentGroup });

      contextDispatch(addTempAnswerToQue(getQAObject));
    }

    if (remove) {
      contextDispatch(removeTempAnswerToQue(remove));
    }
  }

  const answers = get(currentQuestionFlow, `children[${contextState.currentGroup}]`, []);
  /**
   * If the attribute is a function, hand it the answers.
   * Note: Because of inevitable scope creep we didn't anticipate dynamic pricing in real time.
   * We could have created an interface to DATA. Instead, lets intercept.
   */
  const adjustedAnswers = answers.map((answer) => {
    if (typeof answer.override === 'function') {
      return {
        ...answer,
        ...answer.override([...contextState.tempAnswerQue, ...contextState.completedAnswers]),
      };
    }
    return answer;
  });

  switch (currentQuestionFlow.answerType) {
    case CHECKBOX: {
      return (
        <CheckboxGroupV2QA
          onChange={udpateQAFlow}
          onDropdownChange={udpateQAFlow}
          answers={adjustedAnswers}
          complete={currentQuestionFlow.complete}
          required={currentQuestionFlow.required}
          onClickUpdate={updateAnswerTempQue}
          launchAdditionalInfoDisplay={launchAdditionalInfoDisplay}
          layout={currentQuestionFlow.layout}
        />
      );
    }

    case RADIO: {
      return (
        <RadioGroupV2QA
          onChange={udpateQAFlow}
          name={`question${currentQuestionFlow.id}`}
          answers={adjustedAnswers}
          showInput={currentQuestionFlow.showInput}
          launchAdditionalInfoDisplay={launchAdditionalInfoDisplay}
          layout={currentQuestionFlow.layout}
        />
      );
    }

    case TEXTAREA: {
      return (
        <TextAreaV2QAFlow
          type="text"
          placeholder="Type something"
          answers={adjustedAnswers}
          complete={currentQuestionFlow.complete}
          required={currentQuestionFlow.required}
          onButtonClick={udpateQAFlow}
        />
      );
    }
    default: {
      // a default means there is nothing to be derived.
      return null;
    }
  }
};

UISelectionDisplay.propTypes = {
  DATA: PropTypes.object.isRequired,
  previousLocation: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
};

export default React.memo(UISelectionDisplay);
